import { getErrorMessage } from '@/lib/functions';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import * as ImageManipulator from 'expo-image-manipulator';
import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { fontSize, radius, spacing } from '@/constants/theme';
import { useProfile } from '@/contexts/ProfileContext';
import { useTheme } from '@/contexts/ThemeContext';
import { createAnnouncement, getAnnouncementCooldownStatus } from '@/lib/announcements';
import { supabase } from '@/lib/supabase';
import type { AppColors } from '@/constants/theme';

const MAX_CHARS = 280;
const DRAFT_KEY = 'announcement_draft_v1';

export default function NewAnnouncementScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const { profile } = useProfile();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [text, setText] = useState('');
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [paidNotification, setPaidNotification] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [freeAvailableAt, setFreeAvailableAt] = useState<string | null>(null);
  const [paidAvailableAt, setPaidAvailableAt] = useState<string | null>(null);
  const [cooldownLoaded, setCooldownLoaded] = useState(false);

  useEffect(() => {
    void getAnnouncementCooldownStatus()
      .then((status) => {
        setFreeAvailableAt(status.free_available_at);
        setPaidAvailableAt(status.paid_available_at);
        if (status.paid_available_at) setPaidNotification(false);
      })
      .catch(() => {})
      .finally(() => setCooldownLoaded(true));
  }, []);

  // Αν ο χρήστης αφήσει ανοιχτή την οθόνη πέρα από τη λήξη του cooldown, το
  // διακοπτάκι/κουμπί δημοσίευσης πρέπει να ξεκλειδώνουν μόνα τους — χωρίς
  // αυτό, το state έμενε "παγωμένο" στην τιμή του αρχικού fetch για πάντα.
  useEffect(() => {
    if (!freeAvailableAt) return;
    const ms = new Date(freeAvailableAt).getTime() - Date.now();
    if (ms <= 0) { setFreeAvailableAt(null); return; }
    const id = setTimeout(() => setFreeAvailableAt(null), ms);
    return () => clearTimeout(id);
  }, [freeAvailableAt]);

  useEffect(() => {
    if (!paidAvailableAt) return;
    const ms = new Date(paidAvailableAt).getTime() - Date.now();
    if (ms <= 0) { setPaidAvailableAt(null); return; }
    const id = setTimeout(() => setPaidAvailableAt(null), ms);
    return () => clearTimeout(id);
  }, [paidAvailableAt]);

  // Αν ο επαγγελματίας δεν προλάβει να δημοσιεύσει (π.χ. λόγω cooldown), το
  // κείμενο/εικόνα που έγραψε παραμένουν εδώ ώστε να μη χρειαστεί να τα
  // ξαναγράψει όταν επιστρέψει μετά το cooldown.
  useEffect(() => {
    void AsyncStorage.getItem(DRAFT_KEY).then((raw) => {
      if (!raw) return;
      try {
        const draft = JSON.parse(raw) as { text?: string; imageUri?: string | null; imageUrl?: string | null };
        if (draft.text) setText(draft.text);
        if (draft.imageUri) setImageUri(draft.imageUri);
        if (draft.imageUrl) setImageUrl(draft.imageUrl);
      } catch {}
    });
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      if (!text.trim() && !imageUrl) {
        void AsyncStorage.removeItem(DRAFT_KEY);
      } else {
        void AsyncStorage.setItem(DRAFT_KEY, JSON.stringify({ text, imageUri, imageUrl }));
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [text, imageUri, imageUrl]);

  function formatAvailableAt(iso: string): string {
    return new Date(iso).toLocaleString('el-GR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  if (!profile?.portfolio_enabled) {
    return (
      <View style={[styles.container, { paddingTop: insets.top + spacing.lg }]}>
        <Text style={styles.errorPage}>
          {t('announcements.requiresProfessional')}
        </Text>
      </View>
    );
  }

  async function pickImage() {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 1,
      allowsEditing: true,
      aspect: [16, 9],
    });
    if (result.canceled || !result.assets[0]) return;
    const asset = result.assets[0];
    setImageUri(asset.uri);
    setUploadingImage(true);
    setError(null);
    try {
      // Μόνο width — διατηρεί την αναλογία 16:9 του crop (width+height μαζί θα
      // παραμόρφωνε σε τετράγωνο). 800px αρκεί για πλήρες πλάτος οθόνης.
      const compressed = await ImageManipulator.manipulateAsync(
        asset.uri,
        [{ resize: { width: 800 } }],
        { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG, base64: true },
      );
      const path = `${profile!.id}/${Date.now()}.jpg`;
      const { decode } = await import('base64-arraybuffer');
      const { error: upErr } = await supabase.storage
        .from('announcements')
        .upload(path, decode(compressed.base64!), { contentType: 'image/jpeg', cacheControl: '31536000' });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from('announcements').getPublicUrl(path);
      setImageUrl(data.publicUrl);
    } catch (e) {
      setError(t('errors.upload_failed'));
      setImageUri(null);
    } finally {
      setUploadingImage(false);
    }
  }

  async function submit() {
    if (!text.trim()) { setError(t('announcements.error_writeText')); return; }
    setBusy(true); setError(null);
    try {
      await createAnnouncement(text.trim(), imageUrl, paidNotification);
      await AsyncStorage.removeItem(DRAFT_KEY);
      setSuccess(true);
    } catch (e) {
      const msg = getErrorMessage(e, t('common.error'));
      if (msg.startsWith('cooldown:')) {
        const hours = parseFloat(msg.replace('cooldown:', ''));
        setError(t('announcements.error_cooldown', { hours: hours.toFixed(1) }));
      } else if (msg === 'paid_cooldown') {
        setError(t('announcements.error_paid_cooldown'));
      } else if (msg === 'portfolio_not_enabled') {
        setError(t('announcements.requiresProfessionalShort'));
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  if (success) {
    return (
      <View style={[styles.container, styles.successContainer, { paddingTop: insets.top }]}>
        <Ionicons name="checkmark-circle" size={64} color="#10B981" />
        <Text style={styles.successTitle}>{t('announcements.published')}</Text>
        <Text style={styles.successBody}>
          {t('announcements.success_body')}
          {paidNotification ? t('announcements.success_body_push_suffix') : ''}
        </Text>
        <Pressable style={styles.doneBtn} onPress={() => router.back()}>
          <Text style={styles.doneBtnText}>{t('common.done')}</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.header}>
        <Pressable onPress={() => router.back()} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={22} color={colors.text} />
        </Pressable>
        <Text style={styles.headerTitle}>{t('circle.newAnnouncement')}</Text>
        <View style={{ width: 38 }} />
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={{ paddingHorizontal: spacing.lg, paddingBottom: insets.bottom + 80 }}
        keyboardShouldPersistTaps="handled"
      >
        {freeAvailableAt ? (
          <View style={styles.cooldownBanner}>
            <Ionicons name="time-outline" size={15} color={colors.warning} />
            <Text style={styles.cooldownBannerText}>
              {t('announcements.freeCooldownUntil', { date: formatAvailableAt(freeAvailableAt) })}
            </Text>
          </View>
        ) : (
          <View style={styles.freeBanner}>
            <Ionicons name="checkmark-circle-outline" size={15} color="#10B981" />
            <Text style={styles.freeBannerText}>{t('announcements.freeBanner')}</Text>
          </View>
        )}

        <Text style={styles.label}>{t('announcements.field_text')}</Text>
        <View style={styles.textAreaWrapper}>
          <TextInput
            style={styles.textArea}
            value={text}
            onChangeText={(v) => setText(v.slice(0, MAX_CHARS))}
            placeholder={t('announcements.field_text_placeholder')}
            placeholderTextColor={colors.textMuted}
            multiline
            maxLength={MAX_CHARS}
            textAlignVertical="top"
          />
          <Text style={[styles.counter, text.length > MAX_CHARS - 20 && { color: colors.warning }]}>
            {text.length}/{MAX_CHARS}
          </Text>
        </View>

        <Text style={styles.label}>{t('announcements.field_image')}</Text>
        <Pressable style={styles.imagePicker} onPress={pickImage} disabled={uploadingImage}>
          {uploadingImage ? (
            <ActivityIndicator color={colors.primary} />
          ) : imageUri ? (
            <>
              <Image source={{ uri: imageUri }} style={styles.imagePreview} resizeMode="cover" />
              <Pressable style={styles.removeImage} onPress={() => { setImageUri(null); setImageUrl(null); }}>
                <Ionicons name="close-circle" size={22} color="#fff" />
              </Pressable>
            </>
          ) : (
            <View style={styles.imagePickerInner}>
              <Ionicons name="image-outline" size={28} color={colors.textMuted} />
              <Text style={styles.imagePickerText}>{t('announcements.action_pickImage')}</Text>
            </View>
          )}
        </Pressable>

        <Pressable
          style={[styles.toggleRow, paidNotification && styles.toggleRowActive, !!paidAvailableAt && styles.toggleRowDisabled]}
          onPress={() => { if (!paidAvailableAt) setPaidNotification((v) => !v); }}
          disabled={!!paidAvailableAt}
        >
          <View style={styles.toggleLeft}>
            <Ionicons
              name={paidNotification ? 'notifications' : 'notifications-outline'}
              size={20}
              color={paidNotification ? colors.primary : colors.textMuted}
            />
            <View>
              <Text style={[styles.toggleLabel, paidNotification && { color: colors.primary }]}>
                {t('announcements.push_label')}
              </Text>
              <Text style={styles.toggleSub}>
                {paidAvailableAt
                  ? t('announcements.push_availableAt', { date: formatAvailableAt(paidAvailableAt) })
                  : t('announcements.push_limit')}
              </Text>
            </View>
          </View>
          <View style={[styles.toggle, paidNotification && styles.toggleOn]}>
            <View style={[styles.toggleThumb, paidNotification && styles.toggleThumbOn]} />
          </View>
        </Pressable>

        {!!error && (
          <View style={styles.errorBox}>
            <Ionicons name="warning-outline" size={16} color={colors.danger} />
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}
      </ScrollView>

      <View style={[styles.footer, { paddingBottom: insets.bottom + spacing.md }]}>
        <Pressable
          style={[styles.submitBtn, (busy || !text.trim() || !!freeAvailableAt) && styles.submitBtnDisabled]}
          onPress={submit}
          disabled={busy || !text.trim() || !!freeAvailableAt}
        >
          {busy
            ? <ActivityIndicator color="#fff" />
            : <Text style={styles.submitBtnText}>{t('announcements.action_publish')}</Text>
          }
        </Pressable>
      </View>
    </View>
    </KeyboardAvoidingView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    backBtn: { padding: 8 },
    headerTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    scroll: { flex: 1, marginTop: spacing.sm },
    label: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
      marginBottom: spacing.xs,
      marginTop: spacing.md,
    },
    textAreaWrapper: {
      backgroundColor: colors.surface,
      borderRadius: radius.md,
      borderWidth: 1.5,
      borderColor: colors.border,
      padding: spacing.md,
    },
    textArea: {
      color: colors.text,
      fontSize: fontSize.md,
      minHeight: 100,
      lineHeight: 22,
    },
    counter: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      textAlign: 'right',
      marginTop: 4,
    },
    imagePicker: {
      height: 140,
      backgroundColor: colors.surface,
      borderRadius: radius.md,
      borderWidth: 1.5,
      borderColor: colors.border,
      borderStyle: 'dashed',
      overflow: 'hidden',
    },
    imagePickerInner: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.xs,
    },
    imagePickerText: { color: colors.textMuted, fontSize: fontSize.sm },
    imagePreview: { width: '100%', height: '100%' },
    removeImage: {
      position: 'absolute',
      top: 6,
      right: 6,
      backgroundColor: 'rgba(0,0,0,0.5)',
      borderRadius: 12,
    },
    toggleRow: {
      marginTop: spacing.md,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: colors.surface,
      borderRadius: radius.md,
      borderWidth: 1.5,
      borderColor: colors.border,
      padding: spacing.md,
    },
    toggleRowActive: { borderColor: colors.primary },
    toggleRowDisabled: { opacity: 0.5 },
    toggleLeft: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, flex: 1 },
    toggleLabel: { color: colors.text, fontSize: fontSize.sm, fontWeight: '600' },
    toggleSub: { color: colors.textMuted, fontSize: 11, marginTop: 1 },
    toggle: {
      width: 44, height: 24, borderRadius: 12,
      backgroundColor: colors.border,
      justifyContent: 'center',
      paddingHorizontal: 2,
    },
    toggleOn: { backgroundColor: colors.primary },
    toggleThumb: { width: 20, height: 20, borderRadius: 10, backgroundColor: '#fff' },
    toggleThumbOn: { alignSelf: 'flex-end' },
    errorBox: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      marginTop: spacing.md,
      backgroundColor: `${colors.danger}18`,
      borderRadius: radius.sm,
      padding: spacing.sm,
    },
    errorText: { color: colors.danger, fontSize: fontSize.sm, flex: 1 },
    footer: {
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      backgroundColor: colors.background,
    },
    submitBtn: {
      height: 52,
      backgroundColor: colors.primary,
      borderRadius: radius.md,
      alignItems: 'center',
      justifyContent: 'center',
    },
    submitBtnDisabled: { opacity: 0.5 },
    submitBtnText: { color: '#fff', fontSize: fontSize.md, fontWeight: '700' },
    successContainer: { alignItems: 'center', justifyContent: 'center', gap: spacing.md, padding: spacing.xl },
    successTitle: { color: colors.text, fontSize: fontSize.xl, fontWeight: '800' },
    successBody: { color: colors.textMuted, fontSize: fontSize.md, textAlign: 'center', lineHeight: 22 },
    doneBtn: {
      height: 48, backgroundColor: colors.primary, borderRadius: radius.md,
      paddingHorizontal: spacing.xl, alignItems: 'center', justifyContent: 'center',
    },
    doneBtnText: { color: '#fff', fontSize: fontSize.md, fontWeight: '700' },
    freeBanner: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.xs,
      backgroundColor: 'rgba(16,185,129,0.1)',
      borderRadius: radius.md,
      padding: spacing.sm,
      marginTop: spacing.md,
    },
    freeBannerText: {
      color: '#10B981',
      fontSize: fontSize.sm,
      flex: 1,
      lineHeight: 19,
    },
    cooldownBanner: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.xs,
      backgroundColor: `${colors.warning}18`,
      borderRadius: radius.md,
      padding: spacing.sm,
      marginTop: spacing.md,
    },
    cooldownBannerText: {
      color: colors.warning,
      fontSize: fontSize.sm,
      flex: 1,
      lineHeight: 19,
    },
    errorPage: { color: colors.textMuted, fontSize: fontSize.md, textAlign: 'center', padding: spacing.xl },
  });
}
